<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import type { FormMeta, GenericObject } from 'vee-validate';

import FormActions from '@/components/FormActions.vue';
import GuidedConsultationDashboard from '@/components/ConsultationTabs/GuidedConsultationDashboard.vue';
import { Constants } from '@/constants';
import type { ConsultationDto, Locales } from '@/services/types/api.types';
import type { LocaleValidation } from '@/composables/language-validation-errors';

const { t } = useI18n();

const props = defineProps<{
  consultation: ConsultationDto;
  languageEnabled: Locales[];
  onCancel: () => void;
  onSubmit: () => void;
  languageErrors: LocaleValidation;
  meta: FormMeta<GenericObject>;
}>();

const editLocale = defineModel<Locales>('editLocale');

const { value: participationMode, setValue: setParticipationMode } = useField<number>('participation_mode');
const { value: resultsMode, setValue: setResultsMode } = useField<number | undefined>(
  'options.guided_consultation.resultsMode',
);

const isGuidedConsultation = computed(
  () => participationMode.value === Constants.Consultation.ParticipationMode.Guided,
);
</script>

<template>
  <form-actions
    :show-language-switcher="false"
    v-model:editLocale="editLocale"
    :onCancel="props.onCancel"
    :onSubmit="props.onSubmit"
    :languageErrors="props.languageErrors"
    :meta="props.meta"
    :languageEnabled="props.languageEnabled"
  />
  <q-page>
    <template v-if="!isGuidedConsultation">
      <q-card class="q-mb-md bg-blue-1">
        <q-card-section>
          <div class="row no-wrap items-start">
            <q-toggle
              :model-value="isGuidedConsultation"
              color="primary"
              class="q-mt-xs"
              @update:model-value="
                setParticipationMode(
                  $event
                    ? Constants.Consultation.ParticipationMode.Guided
                    : Constants.Consultation.ParticipationMode.Open,
                )
              "
            />
            <div class="col q-ml-sm">
              <div class="text-body1 text-weight-medium">
                {{ t('consultation.guidedConsultation.intro.activateLabel') }}
                <q-badge color="blue-5" class="q-ml-sm text-white text-caption">{{ t('global.new') }}</q-badge>
              </div>
              <div class="text-caption text-grey-7 q-mt-xs">
                {{ t('consultation.guidedConsultation.intro.description') }}
              </div>
            </div>
          </div>
          <div class="q-mt-md q-pl-xl">
            <q-btn
              color="primary"
              :label="t('consultation.guidedConsultation.intro.activateButton')"
              @click="setParticipationMode(Constants.Consultation.ParticipationMode.Guided)"
            />
          </div>
        </q-card-section>
      </q-card>
      <q-card flat class="q-mb-md">
        <q-card-section>
          <q-item-label header class="text-h6 q-pb-sm">
            {{ t('consultation.guidedConsultation.intro.featuresTitle') }}
          </q-item-label>
          <div class="row no-wrap items-start q-mb-sm">
            <q-icon name="o_check_box_outline_blank" class="q-mt-xs text-grey-5" size="xs" />
            <div class="col q-ml-sm">
              <div class="text-body2 text-weight-medium">
                {{ t('consultation.guidedConsultation.intro.features.mandatory.label') }}
              </div>
              <div class="text-caption text-grey-7">
                {{ t('consultation.guidedConsultation.intro.features.mandatory.helper') }}
              </div>
            </div>
          </div>
          <div class="row no-wrap items-start q-mb-sm">
            <q-icon name="o_check_box_outline_blank" class="q-mt-xs text-grey-5" size="xs" />
            <div class="col q-ml-sm">
              <div class="text-body2 text-weight-medium">
                {{ t('consultation.guidedConsultation.intro.features.conditional.label') }}
              </div>
              <div class="text-caption text-grey-7">
                {{ t('consultation.guidedConsultation.intro.features.conditional.helper') }}
              </div>
            </div>
          </div>
          <div class="row no-wrap items-start">
            <q-icon name="o_check_box_outline_blank" class="q-mt-xs text-grey-5" size="xs" />
            <div class="col q-ml-sm">
              <div class="text-body2 text-weight-medium">
                {{ t('consultation.guidedConsultation.intro.features.completion.label') }}
              </div>
              <div class="text-caption text-grey-7">
                {{ t('consultation.guidedConsultation.intro.features.completion.helper') }}
              </div>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </template>
    <template v-else>
      <q-card class="q-mb-md">
        <q-card-section>
          <div class="row no-wrap items-start">
            <q-toggle
              :model-value="isGuidedConsultation"
              color="primary"
              class="q-mt-xs"
              @update:model-value="
                setParticipationMode(
                  $event
                    ? Constants.Consultation.ParticipationMode.Guided
                    : Constants.Consultation.ParticipationMode.Open,
                )
              "
            />
            <div class="col q-ml-sm">
              <div class="text-body1 text-weight-medium">{{ t('consultation.guidedConsultation.active.title') }}</div>
              <div class="text-caption text-grey-7">{{ t('consultation.guidedConsultation.active.description') }}</div>
            </div>
          </div>
        </q-card-section>
      </q-card>
      <q-card class="q-mb-md">
        <q-card-section>
          <q-item-label header class="text-h6">{{
            t('consultation.participationMode.resultsMode.title')
          }}</q-item-label>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="q-mb-sm">
            <div class="row no-wrap items-start">
              <q-radio
                :model-value="resultsMode"
                :val="Constants.Consultation.GuidedConsultation.ResultsMode.CompletedOnly"
                @update:model-value="setResultsMode($event)"
              />
              <div class="col q-ml-sm">
                <div class="text-body2 text-weight-medium">
                  {{ t('consultation.participationMode.resultsMode.completedOnly.label') }}
                </div>
                <div class="text-caption text-grey-7">
                  {{ t('consultation.participationMode.resultsMode.completedOnly.helper') }}
                </div>
              </div>
            </div>
          </div>
          <div>
            <div class="row no-wrap items-start">
              <q-radio
                :model-value="resultsMode"
                :val="Constants.Consultation.GuidedConsultation.ResultsMode.AllResponses"
                @update:model-value="setResultsMode($event)"
              />
              <div class="col q-ml-sm">
                <div class="text-body2 text-weight-medium">
                  {{ t('consultation.participationMode.resultsMode.allResponses.label') }}
                </div>
                <div class="text-caption text-grey-7">
                  {{ t('consultation.participationMode.resultsMode.allResponses.helper') }}
                </div>
              </div>
            </div>
          </div>
        </q-card-section>
      </q-card>
      <guided-consultation-dashboard :consultation="consultation" :language-enabled="languageEnabled" />
    </template>
  </q-page>
</template>
